import { CommonModule } from '@angular/common';
import { Component, signal, type OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { mapSystemStateInfo } from '@/app/services/ia-services/system-state.mapper';
import { SystemsService } from '@/app/services/ia-services/systems.service';
import {
  DeploySystemReviewRole,
  DeploySystemReviewDetail,
  DeploySystemReviewStage,
  DeploySystemReviewUserData,
} from '@/app/interfaces/deploy-system-review';
import { mapSystemTypeLabel } from '@/app/shared/mappers/system-type-label.mapper';
import { getTaskFunctionLabel as mapTaskFunctionLabel } from '@/app/shared/mappers/task-function-label.mapper';
import { mapRiskLevelLabel } from '@/app/shared/mappers/risk-level-label.mapper';
import { SystemDeployRiskManagementComponent } from '../components/risk-management/risk-management.component';
import { SystemDeployRiaComplianceComponent } from '../components/ria-compliance/ria-compliance.component';
import { SystemDeployIsoComplianceComponent } from '../components/iso-compliance/iso-compliance.component';
import { SystemDeployHapComplianceComponent } from '../components/hap-compliance/hap-compliance.component';
import { SystemDeployEconomicDataComponent } from '../components/economic-data/economic-data.component';
import { SystemDeployImpactEvaluationComponent } from '../components/impact-evaluation/impact-evaluation.component';
import { SystemDeployTechnicalIssuesComponent } from '../components/technical-issues/technical-issues.component';
import { BackButtonComponent } from '@/app/shared/back-button/back-button.component';
import { SpanishDatePipe } from '../../../../pipe/date.pipe';
import { PillComponent } from '@/app/shared/pill/pill.component';
import { RiskLevelClassPipe } from '../../../../pipe/riskLevelClass.pipe';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { DesyViewsModule, DesyCommonsModule } from 'desy-angular';
import { ProjectCardComponent } from '@/app/shared/cards/project-card/project-card.component';
import { ReviewStatesComponent } from '@/app/shared/review-states/review-states.component';
import { environment } from '@/environments/environment';

type DeploySystemCorrectionRole = 'responsable_IA' | 'jefe_consumidor';

@Component({
  selector: 'app-deploy-system-of-ai-detail',
  standalone: true,
  imports: [
    CommonModule,
    SystemDeployRiskManagementComponent,
    SystemDeployRiaComplianceComponent,
    SystemDeployIsoComplianceComponent,
    SystemDeployHapComplianceComponent,
    SystemDeployEconomicDataComponent,
    SystemDeployImpactEvaluationComponent,
    SystemDeployTechnicalIssuesComponent,
    BackButtonComponent,
    SpanishDatePipe,
    PillComponent,
    RiskLevelClassPipe,
    ButtonsComponent,
    DesyViewsModule,
    DesyCommonsModule,
    ProjectCardComponent,
    ReviewStatesComponent,
  ],
  templateUrl: './deploy-system-of-ai-detail.component.html',
})
export class DeploySystemOfAiDetailComponent implements OnInit {
  constructor(
    private readonly systemsService: SystemsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  detailDeploySystem: DeploySystemReviewDetail | null = null;
  idSystem: string | null = null;
  userRoles: DeploySystemReviewRole[] = [];
  stateGroups: { group: DeploySystemReviewStage; state: string }[] = [];
  stateTextFormat = signal('');
  colorBox = signal('');
  readonly projectCardFallbackDate = new Date();

  async ngOnInit(): Promise<void> {
    this.idSystem = this.route.snapshot.paramMap.get('id');
    const response = await this.systemsService.getDeploySystemReviewDetail(this.idSystem ?? '');
    if (response?.infoCard) {
      response.infoCard.forEach((card) => {
        card.buttons = [
          {
            label: 'Consultar detalles',
            url: 'catalogo-ia/proyectos/detalles/' + card.id,
          },
        ];
      });
    }
    this.detailDeploySystem = response;
    this.setStateInfo(response.state);
    this.stateGroups = this.buildStateGroups(response);
    this.userRoles = this.getUserRolesForExecution(response);
  }

  getSystemTypeLabel(value: string | null | undefined): string {
    return value ? mapSystemTypeLabel(value) : '-';
  }

  getFunctionLabel(value: string | null | undefined): string {
    return value ? mapTaskFunctionLabel(value) : '-';
  }

  getRiskLevelLabel(value: string | null | undefined): string {
    return mapRiskLevelLabel(value) || '-';
  }

  private setStateInfo(state?: string | null): void {
    const stateInfo = mapSystemStateInfo(state, 'deployment');

    this.stateTextFormat.set(stateInfo.text);
    this.colorBox.set(stateInfo.colorClass);
  }

  canAccessDeployReview(): boolean {
    return this.canCurrentUserAccessReview();
  }

  goToDeployReview(): void {
    if (!this.idSystem || !this.canCurrentUserAccessReview()) {
      return;
    }

    this.router.navigate(['revision'], { relativeTo: this.route });
  }

  isCorrectionState(): boolean {
    return this.detailDeploySystem?.state?.toUpperCase() === 'CORRECTION';
  }

  canCurrentUserCorrect(): boolean {
    if (!this.detailDeploySystem || !this.isCorrectionState()) {
      return false;
    }

    const deployOrganizations = this.getDeployOrganizations(this.detailDeploySystem);

    if (!deployOrganizations.length) {
      return false;
    }

    const userData = this.getStoredUserData();

    return Object.entries(userData.roles ?? {}).some(([role, orgs]) => {
      return (
        this.isDeploySystemCorrectionRole(role) &&
        Array.isArray(orgs) &&
        orgs.some((org) => deployOrganizations.includes(this.normalizeOrgCodeForEnvironment(org)))
      );
    });
  }

  goToCorrection(): void {
    if (!this.idSystem || !this.canCurrentUserCorrect()) {
      return;
    }

    this.router.navigate(['/catalogo-ia', 'sistemas-de-ia', 'despliegue', 'detalles', this.idSystem, 'subsanar']);
  }

  get activeStage(): DeploySystemReviewStage | undefined {
    return this.stateGroups.find((stage) => stage.state === 'PENDING')?.group;
  }

  get reviewStatesButtonLabel(): string {
    return 'Revisar despliegue';
  }

  get reviewButtonLabel(): string {
    if (!this.canCurrentUserReview()) {
      return 'Consultar revisión';
    }

    return 'Revisar sistema desplegado';
  }

  canCurrentUserAccessReview(): boolean {
    return this.canCurrentUserReview() || this.canCurrentUserViewReview();
  }

  canCurrentUserReview(): boolean {
    if (!this.stateGroups.length || !this.detailDeploySystem) {
      return false;
    }

    const activeStage = this.activeStage;

    if (!activeStage) {
      return false;
    }

    switch (activeStage) {
      case 'support':
        return this.userRoles.includes('apoyo_OGIA');
      case 'reviewers':
        return this.getReviewerRolesInWorkflowOrder(this.userRoles).some(
          (role) => this.getReviewStateForUserRole(this.detailDeploySystem, role) === 'PENDING'
        );
      case 'chief':
        return this.userRoles.includes('jefe_servicio_OGIA');
      default:
        return false;
    }
  }

  canCurrentUserViewReview(): boolean {
    if (!this.detailDeploySystem) {
      return false;
    }

    if (this.isCorrectionState()) {
      return this.isCurrentUserReviewRegistered(this.detailDeploySystem);
    }

    if (this.normalizeReviewState(this.detailDeploySystem.serviceManagerState) !== 'PENDING') {
      return false;
    }

    return this.userRoles.some((role) => {
      if (role === 'jefe_servicio_OGIA') {
        return false;
      }

      const state = this.getReviewStateForUserRole(this.detailDeploySystem, role);

      return state === 'APPROVED' || state === 'CORRECTION';
    });
  }

  private isCurrentUserReviewRegistered(detail: DeploySystemReviewDetail): boolean {
    return this.userRoles.some((role) => {
      const state = this.getReviewStateForUserRole(detail, role);

      return state === 'APPROVED' || state === 'CORRECTION';
    });
  }

  private buildStateGroups(detail: DeploySystemReviewDetail): { group: DeploySystemReviewStage; state: string }[] {
    return [
      { group: 'support', state: this.normalizeReviewState(detail.supportGroupState) },
      { group: 'reviewers', state: this.buildReviewersState(detail) },
      { group: 'chief', state: this.normalizeReviewState(detail.serviceManagerState) },
    ];
  }

  private buildReviewersState(detail: DeploySystemReviewDetail): string {
    const states = [
      detail.technicalReviewerState,
      detail.legalReviewerState,
      detail.functionalReviewerState,
      detail.dpdReviewerState,
    ].map((state) => this.normalizeReviewState(state));

    return states.every((state) => state !== 'PENDING') ? 'APPROVED' : 'PENDING';
  }

  private normalizeReviewState(state?: string | null): string {
    return (state ?? 'PENDING').toUpperCase();
  }

  private getUserRolesForExecution(detail: DeploySystemReviewDetail): DeploySystemReviewRole[] {
    const userData = this.getStoredUserData();
    const executionOrgs = [detail.orgCodeCreator]
      .filter((org): org is string => !!org)
      .map((org) => this.normalizeOrgCodeForEnvironment(org));

    return Object.entries(userData.roles ?? {})
      .filter(
        ([, orgs]) =>
          Array.isArray(orgs) && orgs.some((org) => executionOrgs.includes(this.normalizeOrgCodeForEnvironment(org)))
      )
      .map(([role]) => role)
      .filter((role): role is DeploySystemReviewRole => this.isDeploySystemReviewRole(role));
  }

  private getReviewerRolesInWorkflowOrder(
    userRoles: DeploySystemReviewRole[]
  ): DeploySystemReviewRole[] {
    const reviewerRoles: DeploySystemReviewRole[] = [
      'funcional_OGIA',
      'juridico_OGIA',
      'tecnico_OGIA',
      'dpd_OGIA',
    ];

    return reviewerRoles.filter((role) => userRoles.includes(role));
  }

  private getReviewStateForUserRole(detail: DeploySystemReviewDetail, role: DeploySystemReviewRole): string {
    const stateByRole: Record<DeploySystemReviewRole, string | null | undefined> = {
      apoyo_OGIA: detail.supportGroupState,
      funcional_OGIA: detail.functionalReviewerState,
      juridico_OGIA: detail.legalReviewerState,
      tecnico_OGIA: detail.technicalReviewerState,
      dpd_OGIA: detail.dpdReviewerState,
      jefe_servicio_OGIA: detail.serviceManagerState,
    };

    return this.normalizeReviewState(stateByRole[role]);
  }

  private normalizeOrgCodeForEnvironment(orgCode: string): string {
    return orgCode.startsWith(environment.environment) ? orgCode : `${environment.environment}${orgCode}`;
  }

  private getDeployOrganizations(detail: DeploySystemReviewDetail): string[] {
    const detailWithOrgCode = detail as DeploySystemReviewDetail & { codigo_organismo?: string | null };

    return [detailWithOrgCode.codigo_organismo, detail.orgCodeCreator]
      .filter((org): org is string => !!org)
      .map((org) => this.normalizeOrgCodeForEnvironment(org));
  }

  private getStoredUserData(): DeploySystemReviewUserData {
    const storedUserData = localStorage.getItem('userData');

    if (!storedUserData) {
      return {};
    }

    return JSON.parse(storedUserData) as DeploySystemReviewUserData;
  }

  private isDeploySystemReviewRole(role: string): role is DeploySystemReviewRole {
    return (
      role === 'apoyo_OGIA' ||
      role === 'funcional_OGIA' ||
      role === 'juridico_OGIA' ||
      role === 'tecnico_OGIA' ||
      role === 'dpd_OGIA' ||
      role === 'jefe_servicio_OGIA'
    );
  }

  private isDeploySystemCorrectionRole(role: string): role is DeploySystemCorrectionRole {
    return role === 'responsable_IA' || role === 'jefe_consumidor';
  }
}
